import type {
  CategoryComparisonRow,
  FlagQaRecord,
  MerchantRiskRow,
  PeriodSummary,
  QualityBand,
  RiskDimension,
  RiskProblem,
  SummaryRow,
  TrendDataPoint,
  TrendGranularity,
  WeekStartDay,
} from "./flag-qa-types";
import { QUALITY_BAND_THRESHOLDS } from "./flag-qa-types";

/**
 * Determine quality band from F1 score (as percentage 0-100)
 */
export function getQualityBand(f1: number): QualityBand {
  if (f1 < QUALITY_BAND_THRESHOLDS.low) return "low";
  if (f1 < QUALITY_BAND_THRESHOLDS.moderate) return "moderate";
  return "high";
}

/**
 * Collapse multiple edits per flag to single row (first edit)
 * This ensures each flag is counted once for metrics calculation
 */
export function collapseFlagEdits(records: FlagQaRecord[]): FlagQaRecord[] {
  const flagMap = new Map<string, FlagQaRecord>();

  for (const record of records) {
    const existing = flagMap.get(record.id);
    if (!existing) {
      flagMap.set(record.id, record);
    } else if (record.editCreatedAt && existing.editCreatedAt) {
      // Keep the earlier edit
      if (new Date(record.editCreatedAt) < new Date(existing.editCreatedAt)) {
        flagMap.set(record.id, record);
      }
    } else if (record.editCreatedAt && !existing.editCreatedAt) {
      // Prefer record with edit
      flagMap.set(record.id, record);
    }
  }

  return Array.from(flagMap.values());
}

/**
 * Calculate time between flag creation and first edit in hours
 */
export function calculateTimeToAction(flagCreatedAt: string, editCreatedAt: string | null): number | null {
  if (!editCreatedAt) return null;

  const flagDate = new Date(flagCreatedAt);
  const editDate = new Date(editCreatedAt);
  const diffMs = editDate.getTime() - flagDate.getTime();

  return diffMs / (1000 * 60 * 60); // Convert to hours
}

/**
 * Format time to action in human-readable format
 */
export function formatTimeToAction(hours: number | null): string {
  if (hours === null) return "N/A";

  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes}m`;
  }

  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }

  const days = hours / 24;
  return `${days.toFixed(1)}d`;
}

/**
 * Calculate all metrics for a period from collapsed records
 */
export function calculatePeriodSummary(records: FlagQaRecord[]): PeriodSummary {
  const collapsed = collapseFlagEdits(records);

  let fp = 0;
  let tp = 0;
  let fn = 0;
  let reviewedCount = 0;
  const timeToActions: number[] = [];

  for (const record of collapsed) {
    const action = record.actionTaken;

    if (action) {
      reviewedCount++;

      if (action === "flag_dismissed") fp++;
      else if (action === "flag_is_correct") tp++;
      else if (action === "add_flag") fn++;

      const tta = calculateTimeToAction(record.flagCreatedAt, record.editCreatedAt);
      if (tta !== null) {
        timeToActions.push(tta);
      }
    }
  }

  const totalFlags = collapsed.length;
  const recall = tp + fn > 0 ? (100 * tp) / (tp + fn) : 0;
  const precision = tp + fp > 0 ? (100 * tp) / (tp + fp) : 0;
  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
  const accuracy = reviewedCount > 0 ? (100 * tp) / reviewedCount : 0;
  const avgTimeToActionHours =
    timeToActions.length > 0 ? timeToActions.reduce((a, b) => a + b, 0) / timeToActions.length : null;

  return {
    totalFlags,
    reviewedFlags: reviewedCount,
    unreviewedFlags: totalFlags - reviewedCount,
    fp,
    tp,
    fn,
    recall,
    precision,
    f1,
    accuracy,
    avgTimeToActionHours,
    qualityBand: getQualityBand(f1),
  };
}

/**
 * Build summary rows grouped by category (new_flag or risk type)
 */
export function buildSummary(data: FlagQaRecord[]): SummaryRow[] {
  const collapsed = collapseFlagEdits(data);

  const grouped = collapsed.reduce(
    (acc, record) => {
      const category = record.newFlag || record.riskTypeShort || "Unknown";
      const action = record.actionTaken || "unknown";

      if (!acc[category]) {
        acc[category] = { fp: 0, tp: 0, fn: 0 };
      }

      if (action === "flag_dismissed") acc[category].fp += 1;
      else if (action === "flag_is_correct") acc[category].tp += 1;
      else if (action === "add_flag") acc[category].fn += 1;

      return acc;
    },
    {} as Record<string, { fp: number; tp: number; fn: number }>,
  );

  return Object.entries(grouped).map(([category, counts]) => {
    const total = counts.fp + counts.tp + counts.fn;
    const recall = counts.tp + counts.fn > 0 ? (100 * counts.tp) / (counts.tp + counts.fn) : 0;
    const precision = counts.tp + counts.fp > 0 ? (100 * counts.tp) / (counts.tp + counts.fp) : 0;
    const accuracy = total > 0 ? (100 * counts.tp) / total : 0;
    const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;

    return {
      category,
      fp: counts.fp,
      tp: counts.tp,
      fn: counts.fn,
      total,
      recall: Math.round(recall * 100) / 100,
      precision: Math.round(precision * 100) / 100,
      accuracy: Math.round(accuracy * 100) / 100,
      f1: Math.round(f1 * 100) / 100,
    };
  });
}

/**
 * Get the start of week date based on week start day
 */
function getWeekStart(date: Date, weekStartDay: WeekStartDay): Date {
  const d = new Date(date);
  const dayOfWeek = d.getDay();

  if (weekStartDay === "MON") {
    // Monday = 1, so we need to go back (dayOfWeek - 1) days, or 6 days if Sunday
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    d.setDate(d.getDate() + diff);
  } else {
    // Sunday = 0, so we go back dayOfWeek days
    d.setDate(d.getDate() - dayOfWeek);
  }

  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Group records by period for trend analysis
 */
export function groupByPeriod(
  records: FlagQaRecord[],
  granularity: TrendGranularity,
  weekStartDay: WeekStartDay = "MON",
): TrendDataPoint[] {
  const collapsed = collapseFlagEdits(records);

  // Group by period
  const periodMap = new Map<string, FlagQaRecord[]>();

  for (const record of collapsed) {
    const date = new Date(record.flagCreatedAt);
    let periodKey: string;
    let periodStart: Date;
    let periodEnd: Date;

    if (granularity === "weekly") {
      periodStart = getWeekStart(date, weekStartDay);
      periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 6);

      periodKey = `Week of ${periodStart.toISOString().split("T")[0]}`;
    } else {
      // Monthly
      periodStart = new Date(date.getFullYear(), date.getMonth(), 1);
      periodEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      periodKey = periodStart.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    }

    if (!periodMap.has(periodKey)) {
      periodMap.set(periodKey, []);
    }
    periodMap.get(periodKey)?.push(record);
  }

  // Calculate metrics for each period
  const result: TrendDataPoint[] = [];

  for (const [period, periodRecords] of periodMap.entries()) {
    const summary = calculatePeriodSummary(periodRecords);

    // Extract period dates from the first record
    const firstRecord = periodRecords[0];
    const date = new Date(firstRecord.flagCreatedAt);

    let periodStart: string;
    let periodEnd: string;

    if (granularity === "weekly") {
      const startDate = getWeekStart(date, weekStartDay);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);

      periodStart = startDate.toISOString().split("T")[0];
      periodEnd = endDate.toISOString().split("T")[0];
    } else {
      const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      periodStart = startDate.toISOString().split("T")[0];
      periodEnd = endDate.toISOString().split("T")[0];
    }

    result.push({
      period,
      periodStart,
      periodEnd,
      totalFlags: summary.totalFlags,
      reviewedFlags: summary.reviewedFlags,
      f1: summary.f1,
      qualityBand: summary.qualityBand,
      avgTimeToActionHours: summary.avgTimeToActionHours,
    });
  }

  // Sort by period start date
  return result.sort((a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime());
}

/**
 * Calculate delta between two values as percentage
 */
export function calculateDelta(current: number, previous: number): { value: number; trend: "positive" | "negative" } {
  if (previous === 0) {
    if (current === 0) return { value: 0, trend: "positive" };
    return { value: 100, trend: current > 0 ? "positive" : "negative" };
  }

  const delta = ((current - previous) / previous) * 100;
  return {
    value: Math.abs(delta),
    trend: delta >= 0 ? "positive" : "negative",
  };
}

/**
 * Get date range for comparison preset
 */
export function getComparisonDateRange(
  preset: "wow" | "mom",
  referenceDate: Date = new Date(),
): {
  periodA: { start: string; end: string };
  periodB: { start: string; end: string };
} {
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);

  const days = preset === "wow" ? 7 : 30;

  const periodAEnd = new Date(today);
  const periodAStart = new Date(today);
  periodAStart.setDate(today.getDate() - days + 1);

  const periodBEnd = new Date(periodAStart);
  periodBEnd.setDate(periodAStart.getDate() - 1);
  const periodBStart = new Date(periodBEnd);
  periodBStart.setDate(periodBEnd.getDate() - days + 1);

  return {
    periodA: {
      start: periodAStart.toISOString().split("T")[0],
      end: periodAEnd.toISOString().split("T")[0],
    },
    periodB: {
      start: periodBStart.toISOString().split("T")[0],
      end: periodBEnd.toISOString().split("T")[0],
    },
  };
}

/**
 * Filter records by date range
 */
export function filterByDateRange(records: FlagQaRecord[], startDate: string, endDate: string): FlagQaRecord[] {
  const start = new Date(`${startDate}T00:00:00.000Z`);
  const end = new Date(`${endDate}T23:59:59.999Z`);

  return records.filter((record) => {
    const date = new Date(record.flagCreatedAt);
    return date >= start && date <= end;
  });
}

/**
 * Classify where the damage comes from: over-flagging (FP) vs missed detections (FN).
 * Whichever error type is in the majority wins. Mixed is reserved for a genuinely
 * even split (45-55%), not just an unclear one. Null when there are no errors.
 */
export function classifyProblem(fp: number, fn: number): RiskProblem | null {
  const total = fp + fn;
  if (total <= 0) return null;

  const fpShare = fp / total;
  if (fpShare > 0.55) return "falsePositives";
  if (fpShare < 0.45) return "falseNegatives";
  return "mixed";
}

/**
 * Build one row per (merchant, risk) pair, using the same TP/FP/FN definitions as
 * buildSummary so the numbers reconcile with the other tabs. Sorted worst F1 first,
 * ties broken by volume.
 */
export function buildMerchantRiskSummary(records: FlagQaRecord[], dimension: RiskDimension): MerchantRiskRow[] {
  const collapsed = collapseFlagEdits(records);

  const grouped = new Map<string, { merchantId: string; risk: string; fp: number; tp: number; fn: number }>();

  for (const record of collapsed) {
    const risk = record[dimension];
    const action = record.actionTaken;
    if (!risk || !record.merchantId) continue;
    if (action !== "flag_dismissed" && action !== "flag_is_correct" && action !== "add_flag") continue;

    const key = `${record.merchantId} ${risk}`;
    let entry = grouped.get(key);
    if (!entry) {
      entry = { merchantId: record.merchantId, risk, fp: 0, tp: 0, fn: 0 };
      grouped.set(key, entry);
    }

    if (action === "flag_dismissed") entry.fp += 1;
    else if (action === "flag_is_correct") entry.tp += 1;
    else entry.fn += 1;
  }

  const rows: MerchantRiskRow[] = Array.from(grouped.values()).map(({ merchantId, risk, fp, tp, fn }) => {
    const total = fp + tp + fn;
    const precision = tp + fp > 0 ? (100 * tp) / (tp + fp) : 0;
    const recall = tp + fn > 0 ? (100 * tp) / (tp + fn) : 0;
    const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;

    return {
      merchantId,
      risk,
      total,
      fp,
      tp,
      fn,
      precision: Math.round(precision * 100) / 100,
      recall: Math.round(recall * 100) / 100,
      f1: Math.round(f1 * 100) / 100,
      problem: classifyProblem(fp, fn),
    };
  });

  return rows.sort((a, b) => a.f1 - b.f1 || b.total - a.total);
}

export function actionToOutcome(actionTaken: string | null): "TP" | "FP" | "FN" | null {
  if (actionTaken === "flag_is_correct") return "TP";
  if (actionTaken === "flag_dismissed") return "FP";
  if (actionTaken === "add_flag") return "FN";
  return null;
}

export function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug || "na";
}

/**
 * Every flag behind one (merchant, risk) pair, collapsed to one row per flag.
 * reviewedOnly keeps it reconcilable with the summary table, which only counts
 * flags that got a TP/FP/FN outcome.
 */
export function buildFlagDetail(
  records: FlagQaRecord[],
  merchantId: string,
  risk: string,
  dimension: RiskDimension,
  reviewedOnly: boolean,
): FlagQaRecord[] {
  const collapsed = collapseFlagEdits(records);

  return collapsed
    .filter((record) => {
      if (record.merchantId !== merchantId || record[dimension] !== risk) return false;
      if (reviewedOnly && !actionToOutcome(record.actionTaken)) return false;
      return true;
    })
    .sort(
      (a, b) =>
        (actionToOutcome(a.actionTaken) ?? "ZZ").localeCompare(actionToOutcome(b.actionTaken) ?? "ZZ") ||
        new Date(a.flagCreatedAt).getTime() - new Date(b.flagCreatedAt).getTime(),
    );
}

/**
 * Build category comparison between two periods, showing F1 delta
 */
export function buildCategoryComparison(summaryA: SummaryRow[], summaryB: SummaryRow[]): CategoryComparisonRow[] {
  const mapA = new Map(summaryA.map((row) => [row.category, row.f1]));
  const mapB = new Map(summaryB.map((row) => [row.category, row.f1]));

  // Get all unique categories
  const allCategories = new Set([...mapA.keys(), ...mapB.keys()]);

  const result: CategoryComparisonRow[] = [];
  for (const category of allCategories) {
    const f1A = mapA.get(category) ?? 0;
    const f1B = mapB.get(category) ?? 0;
    result.push({
      category,
      f1A,
      f1B,
      f1Delta: f1A - f1B,
    });
  }

  // Sort by F1 delta descending (biggest improvements first)
  return result.sort((a, b) => b.f1Delta - a.f1Delta);
}
